import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDisplaySettings } from '../hooks/useDisplaySettings';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { formatCurrency } from '../lib/formatCurrency';
import { formatStatementDate } from '../lib/formatStatementDate';
import * as portalStatementService from '../services/portalStatementService';
import { CardChevron } from './CardChevron';
import { RecentListRowsSkeleton } from './skeletons';

/**
 * Account-home recent statements (PD-14080 review feedback): latest three
 * with a View all link — fourth tile of the 2×2 recent-activity grid.
 */
export function RecentStatementsCard({
	title,
}: {
	title?: string;
} = {}): ReactElement | null {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const display = useDisplaySettings();
	const { token, activeClientId } = usePortalAuth();

	const { data, isLoading } = useQuery({
		// Key on the active client so the card refetches on an account switch.
		queryKey: ['portal-statements-recent', activeClientId],
		queryFn: () =>
			portalStatementService.getStatements(token ?? '', { page: 1, limit: 3 }),
		enabled: token != null,
		staleTime: 30_000,
	});

	// PD-14691: gate on the statements surface's own flag (independently
	// toggleable per portal since PD-14675; hide-pricing forces it off too).
	// Defensive — the dashboard dispatch already skips mounting this card.
	if (!display.canViewStatements) {
		return null;
	}

	// Skeleton-first (PD-14692): real header + shaped rows. isLoading is
	// false while the query is disabled, so anon visitors fall through.
	if (isLoading) {
		return (
			<div>
				<div className="flex items-center justify-between mb-2">
					<span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
						{t(title ?? 'Recent Statements')}
					</span>
				</div>
				<RecentListRowsSkeleton />
			</div>
		);
	}

	const statements = data?.data ?? [];
	if (statements.length === 0) {
		return null;
	}

	return (
		<div>
			<div className="flex items-center justify-between mb-2">
				<span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
					{t(title ?? 'Recent Statements')}
				</span>
				<button
					type="button"
					onClick={() => {
						void navigate('/statements');
					}}
					className="text-xs text-portal-accent hover:underline"
				>
					{t('View all')}
				</button>
			</div>
			<div className="flex flex-col gap-2">
				{statements.map((statement) => (
					<button
						key={statement.id}
						type="button"
						onClick={() => {
							void navigate(`/statements/${statement.id}`);
						}}
						className="bg-brand-surface border border-brand-border rounded-xl p-3 flex items-start justify-between gap-3 text-left hover:border-portal-accent transition-colors"
					>
						<div className="min-w-0">
							<div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
								{t(statement.statementNumber)}
							</div>
							<div className="text-xs text-brand-muted mt-0.5">
								{t('As of')}{' '}
								{formatStatementDate(statement.asOfDate as unknown as string)}
							</div>
						</div>
						<div className="text-sm font-semibold text-gray-900 dark:text-gray-100 shrink-0">
							{formatCurrency(statement.totalAmountDue)}
						</div>
						<CardChevron />
					</button>
				))}
			</div>
		</div>
	);
}
